import type { AxiosError, CancelToken, Method } from 'axios';
import Axios from 'axios';
import { sleep } from './timing';
import { allExceptionDetails, exceptionEssence, makeCollapsedError } from '../util/exception';
import { invariant } from '../util/invariant';
import { isTimeout } from './timeout';
import type { Executor, RollbackHandler, TaskContextBase, TransientErrorHandler } from './taskContext';
import type { AWSErrorLike } from '../util/awsErrorLike';
import type { ServiceException } from '@smithy/smithy-client';

interface RetryPlanOptions {
  interlude?: (ms: number, cause: any, ct: CancelToken) => Promise<void>;
}

export interface Resilience {
  resiliently<T>(task: () => Promise<T>, ct: CancelToken, beforeRetryInterlude?: (ms: number, cause: any) => void): Promise<T>;
}

async function defaultInterlude(delay: number, _: any, ct: CancelToken) {
  await sleep(delay, ct);
}

export class RetryPlan implements Resilience {
  failureRate = 0;
  static readonly decayFactor = 0.9;

  interlude: (ms: number, cause: any, ct: CancelToken) => Promise<void>;

  constructor(options: RetryPlanOptions = {}) {
    this.interlude = options.interlude ?? defaultInterlude;
  }

  async resiliently<T>(task: () => Promise<T>, ct: CancelToken, beforeRetryInterlude?: (ms: number, cause: any) => void): Promise<T> {
    let delay = 0;
    while (true) {
      const earlierFailureRate = this.failureRate;
      try {
        ct.throwIfRequested();
        const result = await task();
        this.failureRate *= RetryPlan.decayFactor;
        return result;
      } catch (ex) {
        if (Axios.isCancel(ex))
          throw ex;

        this.failureRate = this.failureRate * RetryPlan.decayFactor + 1;

        /*
          Don't judge based on other errors that happened after the task was
          initiated, but allow recovery from spikes in failure rate.
        */
        const effectiveFailureRate = Math.min(this.failureRate, earlierFailureRate);
        if (effectiveFailureRate > 5) {
          throw ex;
        }

        ct.throwIfRequested();

        delay = (delay + 100) * 2;
        const jitteredDelay = delay * (1 + Math.random());

        if (beforeRetryInterlude)
          beforeRetryInterlude(jitteredDelay, ex);

        await this.interlude(jitteredDelay, ex, ct);
      }
    }
  }
}

export const notResilient: Resilience = {
  resiliently<T>(task: () => Promise<T>, ct: CancelToken) {
    return task();
  }
};

/**
 * Determines if an error resulting from an HTTP request is retryable.
 * @param error
 * @param nonRetryableApiMethodsOnAxiosError Api methods that will not be retried if the error is not an explicit overload backpressure.
 * By default POST and PATCH methods are not retried as they are not idempotent.
 */
export function isRetryable(error: unknown, nonRetryableApiMethodsOnAxiosError: Method[] = ['POST', 'PATCH']): boolean {
  const err = error as Partial<AxiosError & NodeJS.ErrnoException & AWSErrorLike & ServiceException>;

  const status = err.response?.status;
  const data = err.response?.data;

  // Explicit overload backpressure is retryable.

  if(err.name == 'ThrottlingException') {
    return true;
  }

  if (err.retryable !== undefined) {
    return err.retryable;
  }

  if (err.$retryable !== undefined) {
    return true;
  }

  if (status == 500) {
    /*
      S3 sometimes returns a 500 error message, containing a text message asking
      the client to try again.
    */
    if (typeof data === 'string' && data.includes('Please try again.</Message>')) {
      return true;
    }

    console.warn('Warning: Considering unknown 500 error as retryable.', allExceptionDetails(error), data);
    return true;
  }

  if (status == 502 || status == 503 || status == 504 || status == 429) {
    return true;
  }

  if (err.isAxiosError) {
    const aerr = err as AxiosError;
    const method = aerr.config?.method;
    if (method && nonRetryableApiMethodsOnAxiosError.includes(method)) {
      return false;
    }

    if (aerr.message == 'Network Error') {
      // E.g. if the network adapter is disabled, or something similar.
      return true;
    }
  }

  // TCP connect timeout
  if (err.code == 'ETIMEDOUT') {
    return true;
  }

  // Axios timeout exceeded
  if (err.code == 'ECONNABORTED') {
    return true;
  }

  if (err.code == 'ECONNREFUSED') {
    return true;
  }

  if (err.code == 'EPROTO') {
    return true;
  }

  // DNS lookup timeout
  if (err.code == 'EAI_AGAIN') {
    return true;
  }

  /*
    We've observed that some DNS servers lie, and will claim that a host does
    not exist, when in fact the DNS server is unable to determine whether it
    exists or not. I.e. an ENOTFOUND error from getaddrinfo may in fact be an a
    transient upstream network outage.
  */
  if (err.syscall == 'getaddrinfo' && err.code == 'ENOTFOUND') {
    return true;
  }

  // Lost TCP/IP connection
  if (err.code == 'ECONNRESET') {
    return true;
  }

  // Connection unexpectedly closed by other end
  if (err.code == 'EPIPE') {
    return true;
  }

  // Operation timed out (possibly due to zombie connection)
  if (isTimeout(err)) {
    return true;
  }

  if (status == null) {
    return false;
  }

  if (status == 400) {
    // S3 blob write timeout
    if (typeof data === 'string' && data.includes('<Error><Code>RequestTimeout</Code>')) {
      return true;
    }
  }

  return false;
}

const RETRY_DELAY_EXPONENTIATION_BASE = 2;
const SECOND_RETRY_DELAY = 1000;

// First delay will be 0
// Second delay will be a random value in range "(SECOND_RETRY_DELAY * 0.5, SECOND_RETRY_DELAY * 1]"
// e.g., if SECOND_RETRY_DELAY is 1000 second delay will be > 500 and <= 1000
// From there, the delay will roughly multiply by RETRY_DELAY_EXPONENTIATION_BASE each time.
// the delay will be multiplied by a random value in range "(RETRY_DELAY_EXPONENTIATION_BASE - 0.1, RETRY_DELAY_EXPONENTIATION_BASE]"
// e.g., if RETRY_DELAY_EXPONENTIATION_BASE is 2 delay will be multiplied by a value > 1.9 and <= 2
export function getJitteredDelay(failureCount: number, totalDelay: number, randomFunc: () => number) {
  invariant(failureCount >= 1, 'Expected failure count to be at least 1');
  if (failureCount === 1) {
    return 0;
  }

  if (failureCount === 2) {
    return SECOND_RETRY_DELAY * (1 - randomFunc() / 2);
  }

  invariant(totalDelay > 0, 'Total delay should be greater than 0');
  return totalDelay * (RETRY_DELAY_EXPONENTIATION_BASE - randomFunc() / 10);
}

export interface ExponentialBackoffOptions extends RetryPlanOptions {
  timeLimit?: number;
  /**
   * If you want to use retryCount, use it with timeLimit Infinity so that
   * the default timeLimit isn't hit before the retryCount.
   */
  retryCount?: number;
  isRetryable?: (err: unknown) => boolean
}

/**
 * Tracks exponential backoff of a single high-level operation.
 *
 * This class intentionally does not offer a reset() operation, since if we're
 * polling an API we do not want a successful call following a failed one to
 * reset the delay time or retry count.
 */
export class ExponentialBackoff implements Resilience, Executor {
  interlude: (ms: number, cause: any, ct: CancelToken) => Promise<void>;
  isRetryable: (err: unknown) => boolean;
  timeLimit: number;
  retryCount: number;

  constructor(options: ExponentialBackoffOptions = {}) {
    this.interlude = options.interlude ?? defaultInterlude;
    this.isRetryable = options.isRetryable ?? isRetryable;
    this.timeLimit = options.timeLimit ?? 10000; // 10 seconds
    this.retryCount = options.retryCount ?? Infinity;
  }

  run<T>(context: TaskContextBase, task: () => Promise<T>) {
    return this.resiliently(task, context.getCancelToken(), context.onRollback, context);
  }

  async resiliently<T>(task: () => Promise<T>, ct: CancelToken, onRollback?: RollbackHandler, context?: TaskContextBase): Promise<T> {
    invariant(ct, 'A CancelToken is required');
    let failures = 0;
    let totalDelay = 0;
    let significantError: any;

    const transientErrorHandlers: TransientErrorHandler[] = context?.transientErrorHandlers ?? [];
    let clearActions: (() => void)[] = [];
    const clearTransientErrors = () => {
      for (const ca of clearActions) {
        try {
          ca();
        } catch (ex2) {
          console.log('clearTransientError failed', ex2);
        }
      }
      clearActions = [];
    };
    try {
      while (true) {
        try {
          ct.throwIfRequested();
          return await task();
        } catch (ex) {
          if (Axios.isCancel(ex)) {
            throw significantError ?? ex;
          }

          significantError = ex;
          if (!this.isRetryable(ex)) {
            throw ex;
          }

          failures++;

          ct.throwIfRequested();

          const jitteredDelay = getJitteredDelay(failures, totalDelay, Math.random);
          invariant(jitteredDelay != Infinity, 'Jitter delay must not be infinity');

          totalDelay += jitteredDelay;

          if (totalDelay > this.timeLimit || failures > this.retryCount) {
            throw makeCollapsedError('RetriesExhausted', exceptionEssence(ex) + ', after ' + failures + ' failed operations in the span of ' + Math.ceil(totalDelay) + 'ms', { cause: ex, info: { failures } });
          }

          clearTransientErrors();
          for (const teh of transientErrorHandlers) {
            try {
              const cookie = teh.setTransientError(ex, failures, jitteredDelay);
              clearActions.push(() => {
                teh.clearTransientError(cookie);
              });
            } catch (ex2) {
              console.log('setTransientError failed', ex2);
            }
          }

          if (onRollback) {
            onRollback(jitteredDelay, ex, failures);
          }

          await this.interlude(jitteredDelay, ex, ct);
        }
      }
    } finally {
      clearTransientErrors();
    }
  }
}

/** First delay will be 0
 *
 * second delay will be > 500 and <= 1000
 *
 * From there, the delay will roughly multiply by a value > 1.9 and <= 2
*/
export function getJitteredDelayForExponentialBackOff(failCount: number, lastDelay: number) {
  let jitteredDelay = 0;
  if (failCount == 1) {
    jitteredDelay = 500 * (1 - Math.random() / 2);
  } else if (failCount > 1) {
    jitteredDelay = lastDelay * (2 - Math.random() / 10);
  }
  return jitteredDelay;
}
